import type { BadgeDefinition } from '@/domain/badge'

// P1 badge catalog — meaningful cybersecurity achievements tied to the
// three real seeded episodes, plus one capstone badge that exercises the
// "requiredEpisodeIds" rule. QR Code Hunter / AI Detective (mentioned as
// examples in the P1 brief) are good candidates once matching episodes
// exist in the catalog — deliberately not added yet since there's no
// content behind them (see P1 Completion Report).
export const seedBadgeDefinitions: BadgeDefinition[] = [
  {
    id: 'badge-phishing-defender',
    badgeKey: 'phishing-defender',
    name: 'Phishing Defender',
    description: 'Passed "The Phishing Trap" — spotted a phishing attempt before it did damage.',
    iconEmoji: '🎣',
    ruleEvent: 'quiz_passed',
    ruleThreshold: { episodeId: 'ep-phishing-trap' },
  },
  {
    id: 'badge-scam-spotter',
    badgeKey: 'scam-spotter',
    name: 'Scam Spotter',
    description: 'Passed "SIM Swap Shadows" — recognized the early warning signs of SIM-swap fraud.',
    iconEmoji: '🕵️',
    ruleEvent: 'quiz_passed',
    ruleThreshold: { episodeId: 'ep-simswap-shadows' },
  },
  {
    id: 'badge-password-protector',
    badgeKey: 'password-protector',
    name: 'Password Protector',
    description: 'Passed "Password Fortress" — built the habits that make a password fortress.',
    iconEmoji: '🔐',
    ruleEvent: 'quiz_passed',
    ruleThreshold: { episodeId: 'ep-password-fortress' },
  },
  {
    id: 'badge-cyber-aware',
    badgeKey: 'cyber-aware',
    name: 'Cyber Aware',
    description: 'Passed every episode quiz in the CyberTales Learning foundations track.',
    iconEmoji: '🛡️',
    ruleEvent: 'quiz_passed',
    ruleThreshold: {
      requiredEpisodeIds: ['ep-phishing-trap', 'ep-simswap-shadows', 'ep-password-fortress'],
    },
  },

  // P4 achievements — same BadgeDefinition/BadgeAward system as above,
  // extended to episode/scenario/story/escape-room/streak events (Document
  // 11 §P4). Only added for features that actually exist in this build —
  // no achievements for P5-only content.
  {
    id: 'badge-first-mission',
    badgeKey: 'first-mission',
    name: 'First Mission',
    description: 'Completed your first CyberTales episode.',
    iconEmoji: '🚀',
    ruleEvent: 'episode_completed',
    ruleThreshold: { anyEpisode: true },
  },
  {
    id: 'badge-quiz-master',
    badgeKey: 'quiz-master',
    name: 'Quiz Master',
    description: 'Passed 3 or more episode quizzes.',
    iconEmoji: '🧠',
    ruleEvent: 'quiz_passed',
    ruleThreshold: { minQuizzesPassed: 3 },
  },
  {
    id: 'badge-scenario-solver',
    badgeKey: 'scenario-solver',
    name: 'Scenario Solver',
    description: "Completed 3 or more of Sarah's practice scenarios.",
    iconEmoji: '🧩',
    ruleEvent: 'scenario_completed',
    ruleThreshold: { minScenariosCompleted: 3 },
  },
  {
    id: 'badge-story-voyager',
    badgeKey: 'story-voyager',
    name: 'Story Voyager',
    description: 'Made it through an Interactive Story safely, choice by choice.',
    iconEmoji: '📖',
    ruleEvent: 'story_completed',
    ruleThreshold: { storyId: 'story-phishing-urgent-verify', requireGoodOutcome: true },
  },
  {
    id: 'badge-escape-artist',
    badgeKey: 'escape-artist',
    name: 'Escape Artist',
    description: 'Escaped the Cybersecurity Escape Room before time ran out.',
    iconEmoji: '🗝️',
    ruleEvent: 'escape_room_completed',
    ruleThreshold: { escapeRoomId: 'room-breach-at-demo-enterprise' },
  },
  {
    id: 'badge-on-a-roll',
    badgeKey: 'on-a-roll',
    name: 'On a Roll',
    description: 'Kept up a 3-day learning streak.',
    iconEmoji: '🔥',
    ruleEvent: 'streak_milestone',
    ruleThreshold: { minStreakDays: 3 },
  },
]
